// Fix for inconsistent font styling when redirecting from email links.
// Sometimes the font or its weight wasn't applied correctly after validation redirect.
import "../App.css";
import "../index.css";
import React, {
  useEffect,
  useState,
  useCallback,
  Suspense,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLogout } from "react-admin";
import { useNavigate } from "react-router-dom";

import { ApiService } from "../services/AutoOnboardingApiService";
import PasswordChangeDialog from "./PasswordDialog";
import CompletedForm from "./CompletedForm";
import StepIndicator from "./StepIndicator";
import { useToast } from "../components/ui/toast";
import { ROLE_TYPES } from "../enums/Roles";
import { TOTAL_STEPS, COMPLETED_STEP, STEPS } from "../constants/Onboarding";
import { MIXPANEL_EVENTS } from "../constants/MixPanel";
import { trackEvent } from "../services/MixpanelService";
import { LightSurface } from "../common/LightSurface";

// Lazy loaded step components
const GeneralInformation = React.lazy(() => import("./GeneralInformation"));
const BusinessDataForm = React.lazy(() => import("./BusinessData"));
const ProductsForm = React.lazy(() => import("./Products"));

// Loading indicator
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

const checkPassword = async (setShowPasswordDialog, checkPasswordRef) => {
  if (checkPasswordRef.current) return;
  const response = await ApiService.isUserConfirmed();
  if (response?.isPasswordUserEmail) {
    setShowPasswordDialog(true);
  }
  checkPasswordRef.current = true;
};

// Main component
const MultiStepForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isFormDataLoading, setIsFormDataLoading] = useState(true);
  const [formOptions, setFormOptions] = useState({
    placeTypes: [],
    placeTargets: [],
    categories: [],
    products: [],
  });
  const token = localStorage.getItem("token");
  const checkPasswordRef = useRef(false);

  const [restaurantData, setRestaurantData] = useState(null);
  const { showToast } = useToast();
  const logout = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role !== ROLE_TYPES.RESTAURANT_DRAFT) {
      navigate("/#/login");
    }
  }, [logout, navigate]);

  // Load restaurant data
  useEffect(() => {
    if (token && !restaurantData) {
      setIsFormDataLoading(true);
      ApiService.fetchRestaurant()
        .then((data) => {
          if (!data) {
            showToast("Error al obtener el restaurante", "error");
            return;
          }
          setRestaurantData(data);
          if (data.onboardingCompleted) {
            setCurrentStep(COMPLETED_STEP);
            setIsCompleted(true);
            return;
          }
        })
        .finally(() => {
          setIsFormDataLoading(false);
        });
    }
  }, [token, restaurantData, showToast]);

  useEffect(() => {
    trackEvent(MIXPANEL_EVENTS.VIEW_STEP, {
      step: STEPS[currentStep],
    });
  }, [currentStep]);

  const fetchingData = useRef(false);

  // Load form options data (placeTypes, placeTargets, etc.)
  useEffect(() => {
    try {
      if (
        token &&
        !fetchingData.current &&
        !formOptions.placeTypes.length &&
        !formOptions.placeTargets.length &&
        !formOptions.categories.length
      ) {
        setIsFormDataLoading(true);
        const fetchData = async () => {
          fetchingData.current = true;
          const data = await ApiService.fetchDataToPopulateForm();
          setFormOptions(data);
        };

        fetchData().finally(() => {
          setIsFormDataLoading(false);
        });
      }
    } catch (error) {
      console.error("Error al obtener los datos del formulario", error);
    } finally {
      fetchingData.current = false;
    }
  }, [token, formOptions]);

  useEffect(() => {
    if (token && !checkPasswordRef.current) {
      checkPassword(setShowPasswordDialog, checkPasswordRef);
    }
  }, [token]);

  const handleStepChange = useCallback(
    (newStep) => {
      if (newStep !== currentStep) {
        setCurrentStep(newStep);
      }
    },
    [currentStep]
  );

  // Handle step navigation
  const nextStep = useCallback(async () => {
    if (currentStep < TOTAL_STEPS) {
      // scroll to top with smooth behavior
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      await new Promise((resolve) => setTimeout(resolve, 500));
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(async () => {
    if (currentStep > 1) {
      //  scroll to top with smooth behavior
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setIsFormDataLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setCurrentStep((prev) => prev - 1);
      setIsFormDataLoading(false);
    }
  }, [currentStep]);

  // Memoized render function for password dialog
  const renderPasswordDialog = useCallback(() => {
    return (
      <PasswordChangeDialog
        showDialog={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onPasswordChange={() => {}} // Add any callback needed after password change
      />
    );
  }, [showPasswordDialog]);

  // Memoized render function for current step
  const renderCurrentStep = useCallback(() => {
    if (isCompleted) {
      return <CompletedForm restaurantData={restaurantData} />;
    }

    switch (currentStep) {
      case 1:
        return (
          <GeneralInformation
            currentStep={currentStep}
            prevStep={prevStep}
            nextStep={nextStep}
            totalSteps={TOTAL_STEPS}
            setIsCompleted={setIsCompleted}
            restaurantData={restaurantData}
            setRestaurantData={setRestaurantData}
          />
        );
      case 2:
        return (
          <BusinessDataForm
            placeTypes={formOptions.placeTypes}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            prevStep={prevStep}
            nextStep={nextStep}
            totalSteps={TOTAL_STEPS}
            setIsCompleted={setIsCompleted}
            restaurantData={restaurantData}
            setRestaurantData={setRestaurantData}
          />
        );
      case 3:
        return (
          <ProductsForm
            categories={formOptions.categories}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onStepChange={handleStepChange}
            restaurantData={restaurantData}
            setFormOptions={setFormOptions}
            formOptions={formOptions}
            setIsCompleted={setIsCompleted}
            isCompleted={isCompleted}
            setStep={setCurrentStep}
          />
        );
      default:
        return null;
    }
  }, [
    currentStep,
    formOptions,
    isCompleted,
    restaurantData,
    handleStepChange,
    nextStep,
    prevStep,
  ]);

  return (
    <LightSurface>
      <div
        className="w-full mx-auto h-screen bg-neutralLight"
        style={{ colorScheme: "light" }}
      >
        {renderPasswordDialog()}
        <div className="flex flex-col lg:flex-row h-full">
          <>
            <StepIndicator
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              isFormDataLoading={isFormDataLoading}
            />
            <div className="w-full lg:w-3/4 lg:h-full rounded-lg shadow-sm flex flex-col">
              <AnimatePresence mode="wait" initial={false} custom={currentStep}>
                <motion.div
                  key={currentStep}
                  custom={currentStep}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    duration: 0,
                  }}
                  style={{ scrollbarWidth: "thin" }}
                  className="lg:h-screen flex-grow lg:overflow-y-auto"
                >
                  <Suspense fallback={<LoadingFallback />}>
                    {isFormDataLoading ? (
                      <LoadingFallback />
                    ) : (
                      renderCurrentStep()
                    )}
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </div>
          </>
        </div>
      </div>
    </LightSurface>
  );
};

export default MultiStepForm;
